import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

const APP_URL = 'https://www.g2egente.com.br/app';
const TEAL = [14, 116, 144];
const DARK = [15, 23, 42];
const GREY = [100, 116, 139];
const PAGE_H = 297;
const SIDE_W = 68;
const MAIN_X = SIDE_W + 10;
const MAIN_W = 210 - MAIN_X - 10;
const CONTENT_BOTTOM = 265;

const fmtPeriod = (v) => {
  if (!v) return '';
  const m = String(v).match(/^(\d{4})-(\d{2})/);
  return m ? `${m[2]}/${m[1]}` : String(v);
};

export const ageFrom = (birth) => {
  if (!birth) return null;
  const b = new Date(`${String(birth).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(b.getTime())) return null;
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) age -= 1;
  return age;
};

const loadPhoto = async (url) => {
  if (!url) return null;
  try {
    const full = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    const blob = await (await fetch(full)).blob();
    if (!blob.type.startsWith('image/')) return null;
    return await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const size = Math.min(img.width, img.height);
        const c = document.createElement('canvas');
        c.width = 360; c.height = 360;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#0e7490'; ctx.fillRect(0, 0, 360, 360);
        ctx.beginPath(); ctx.arc(180, 180, 180, 0, Math.PI * 2); ctx.clip();
        ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, 360, 360);
        resolve(c.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(blob);
    });
  } catch { return null; }
};

const drawSidebarBg = (doc) => {
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, SIDE_W, PAGE_H, 'F');
};

const drawFooter = (doc, qr) => {
  doc.addImage(qr, 'PNG', 185, PAGE_H - 25, 20, 20);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...TEAL);
  doc.text('www.G2eGente.com.br', 182, PAGE_H - 9, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...GREY);
  doc.text('Currículo gerado pelo app G2eGente', 182, PAGE_H - 5.5, { align: 'right' });
};

const sideSection = (doc, title, y) => {
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(255, 255, 255);
  doc.text(title.toUpperCase(), 8, y);
  doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.3); doc.line(8, y + 1.5, SIDE_W - 8, y + 1.5);
  return y + 7;
};

const sideLines = (doc, lines, y) => {
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(255, 255, 255);
  lines.forEach((l) => {
    if (y > CONTENT_BOTTOM + 15) return;
    const atBreak = l.includes('@') && doc.getTextWidth(l) > SIDE_W - 16 ? [l.slice(0, l.indexOf('@') + 1), l.slice(l.indexOf('@') + 1)] : l;
    const wrapped = doc.splitTextToSize(atBreak, SIDE_W - 16);
    doc.text(wrapped, 8, y);
    y += wrapped.length * 4 + 1.5;
  });
  return y + 3;
};

const drawSidebar = (doc, p, photo, email) => {
  let y = 14;
  if (photo) { doc.addImage(photo, 'JPEG', (SIDE_W - 38) / 2, y, 38, 38); y += 46; }
  const age = ageFrom(p.data_nascimento);
  const contato = [p.telefone, email, p.cidade && `${p.cidade}${p.estado ? `/${p.estado}` : ''}`, age && `${age} anos`].filter(Boolean);
  if (contato.length) { y = sideSection(doc, 'Contato', y); y = sideLines(doc, contato, y); }
  if (p.competencias?.length) { y = sideSection(doc, 'Competências', y); y = sideLines(doc, p.competencias.slice(0, 18).map((c) => `• ${c}`), y); }
  const extras = [p.cnh && p.cnh !== 'nao_possui' && `CNH ${p.cnh}`, p.disponibilidade_mudanca && 'Disponível para mudança', p.is_pcd && 'Pessoa com deficiência'].filter(Boolean);
  if (extras.length && y < CONTENT_BOTTOM - 20) { y = sideSection(doc, 'Informações', y); sideLines(doc, extras, y); }
};

export async function buildResumePdf(profile, email) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const qr = await QRCode.toDataURL(APP_URL, { margin: 0, width: 300, color: { dark: '#0e7490', light: '#ffffff' } });
  const photo = await loadPhoto(profile.foto_url);
  drawSidebarBg(doc);
  drawSidebar(doc, profile, photo, email);
  let y = 22;

  const ensure = (h) => {
    if (y + h <= CONTENT_BOTTOM) return;
    drawFooter(doc, qr);
    doc.addPage(); drawSidebarBg(doc); y = 18;
  };
  const section = (title) => {
    ensure(14);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...TEAL);
    doc.text(title.toUpperCase(), MAIN_X, y);
    doc.setDrawColor(...TEAL); doc.setLineWidth(0.5); doc.line(MAIN_X, y + 1.8, MAIN_X + MAIN_W, y + 1.8);
    y += 8;
  };
  const para = (text, size = 9.5, color = DARK, style = 'normal') => {
    const font = () => { doc.setFont('helvetica', style); doc.setFontSize(size); doc.setTextColor(...color); };
    font();
    const lines = doc.splitTextToSize(String(text), MAIN_W);
    lines.forEach((l) => { ensure(5); font(); doc.text(l, MAIN_X, y); y += size * 0.45; });
    y += 1;
  };

  doc.setFont('helvetica', 'bold'); doc.setFontSize(22); doc.setTextColor(...DARK);
  doc.splitTextToSize(profile.nome || '', MAIN_W).forEach((l) => { doc.text(l, MAIN_X, y); y += 9; });
  const cargos = (profile.cargos?.length ? profile.cargos.map((c) => c.titulo) : [profile.cargo]).filter(Boolean);
  if (cargos.length) para(cargos.join(' | '), 11, TEAL, 'bold');
  y += 4;

  if (profile.resumo) { section('Resumo profissional'); para(profile.resumo); y += 3; }

  if (profile.experiencias?.length) {
    section('Experiência profissional');
    profile.experiencias.forEach((e) => {
      doc.setFontSize(9.5);
      const descLines = e.descricao ? doc.splitTextToSize(String(e.descricao), MAIN_W).length : 0;
      ensure(Math.min(14 + descLines * 4.3, 40));
      para(e.cargo, 10.5, DARK, 'bold');
      const fim = e.atual ? 'atual' : fmtPeriod(e.periodo_fim);
      para(`${e.empresa}  ·  ${fmtPeriod(e.periodo_inicio)}${fim ? ` – ${fim}` : ''}`, 9, GREY);
      if (e.descricao) para(e.descricao);
      y += 2.5;
    });
  }

  if (profile.formacoes?.length) {
    section('Formação');
    profile.formacoes.forEach((f) => {
      ensure(10);
      para(`${f.nivel ? `${f.nivel} – ` : ''}${f.curso}`, 10, DARK, 'bold');
      para(`${f.instituicao}${f.em_andamento ? '  ·  em andamento' : f.ano_conclusao ? `  ·  ${f.ano_conclusao}` : ''}`, 9, GREY);
      y += 1.5;
    });
  }

  if (profile.cursos?.length) {
    section('Cursos e certificações');
    profile.cursos.forEach((c) => {
      para(`• ${c.nome}${c.instituicao ? ` – ${c.instituicao}` : ''}${c.ano ? ` (${c.ano})` : ''}${c.carga_horaria ? ` · ${c.carga_horaria}` : ''}`, 9.5);
    });
  }

  drawFooter(doc, qr);
  const name = (profile.nome || 'curriculo').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_');
  doc.save(`Curriculo_${name}.pdf`);
}
